<template>
  <b-sidebar
    id="add-new-service-sidebar"
    :visible="isSideBarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    :no-close-on-backdrop="submitServiceBtnLoading"
    :no-close-on-esc="submitServiceBtnLoading"
    @hidden="resetForm"
    @change="(val) => $emit('update:isSideBarActive', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ isNewService? 'Add New Service' : 'Edit Service' }}
        </h5>

        <feather-icon
          v-if="!submitServiceBtnLoading"
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          autocomplete="off"
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Service -->
          <validation-provider
            #default="validationContext"
            name="Service"
            rules="required|alpha-dash|max:15"
          >
            <b-form-group
              label="Service"
              label-for="service"
            >
              <b-form-input
                id="service"
                v-model="serviceData.service"
                :readonly="!isNewService"
                :state="getValidationState(validationContext)"
                trim
                @input="serviceData.service = serviceData.service.toLowerCase()"
                @keypress="maxCharacters($event, 15)"
                @keydown.space="event => event.preventDefault()"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Service Label"
            rules="required|max:15"
          >
            <b-form-group
              label="Service Label"
              label-for="service-label"
            >
              <b-form-input
                id="service-label"
                v-model="serviceData.serviceLabel"
                :state="getValidationState(validationContext)"
                trim
                @keypress="maxCharacters($event, 15)"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Class"
            rules="required|alpha-dash|max:15"
          >
            <b-form-group
              label="Class"
              label-for="class"
            >
              <b-form-input
                id="class"
                v-model="serviceData.class"
                :readonly="!isNewService"
                :state="getValidationState(validationContext)"
                trim
                @input="serviceData.class = serviceData.class.toLowerCase()"
                @keypress="maxCharacters($event, 15)"
                @keydown.space="event => event.preventDefault()"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Class Label"
            rules="required|max:15"
          >
            <b-form-group
              label="Class Label"
              label-for="class-label"
            >
              <b-form-input
                id="class-label"
                v-model="serviceData.classLabel"
                :state="getValidationState(validationContext)"
                trim
                @keypress="maxCharacters($event, 15)"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Icon"
            :rules="isNewService ? 'image': 'image'"
          >
            <b-form-group
              label="Icon"
              label-for="icon"
            >
              <b-form-file
                :id="'icon' + isSideBarActive"
                v-model="icon"
                accept="image/*"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                :state="getValidationState(validationContext)"
                @change="iconChange( $event )"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Status"
            rules="required"
          >
            <b-form-group
              label="Status"
              label-for="status"
            >
              <b-form-radio-group
                id="status"
                v-model="serviceData.status"
                :options="statusOptions"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <b-form-group
            label="Page Type"
            label-for="page-type"
          >
            <v-select
              id="page-type"
              v-model="serviceData.pageType"
              :options="pageTypes"
              :clearable="false"
              :reduce="val => val.value"
            />
          </b-form-group>

          <b-form-group
            label="Home Order"
            label-for="home-order"
          >
            <b-form-input
              id="home-order"
              v-model="serviceData.homeOrder"
              type="number"
            />
          </b-form-group>

          <b-form-group
            label="Ride Order"
            label-for="ride-order"
          >
            <b-form-input
              id="ride-order"
              v-model="serviceData.rideOrder"
              type="number"
            />
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="submitServiceBtnLoading"
            >
              <b-spinner
                v-if="submitServiceBtnLoading"
                small
              />
              <span v-else>{{ isNewService? 'Add' : 'Edit' }}</span>

            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              :disabled="submitServiceBtnLoading"
              @click="hide"
            >
              <b-spinner
                v-if="submitServiceBtnLoading"
                small
              />
              <span v-else>Cancel</span>
            </b-button>
          </div>

        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton, BFormFile, BFormRadioGroup, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, watch } from '@vue/composition-api'
import { required, alphaDash, regex } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { max, image, maxCharacters } from '../../utils/validations'
import store from '@/store'

export default {
  components: {
    BSpinner,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormFile,
    vSelect,
    BFormRadioGroup,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isSideBarActive',
    event: 'update:isSideBarActive',
  },
  props: {
    isNewService: {
      type: Boolean,
      default: true,
    },
    editServiceData: {
      type: Object,
      default: () => ({}),
    },
    isSideBarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaDash,
      regex,
      max,
      image,
    }
  },
  setup(props, { emit }) {
    const pageTypes = [
      {
        label: 'Ride',
        value: 'ride',
      },
      {
        label: 'Food',
        value: 'food',
      },
    ]
    const blankServiceData = {
      service: '',
      serviceLabel: '',
      class: '',
      classLabel: '',
      iconUrl: '',
      status: 'INACTIVE',
      homeOrder: 0,
      rideOrder: 0,
      pageType: 'ride',
    }
    const serviceData = ref(JSON.parse(JSON.stringify(blankServiceData)))
    const icon = ref(null)
    const resetServiceData = () => {
      serviceData.value = JSON.parse(JSON.stringify(blankServiceData))
    }
    watch(() => props.isSideBarActive, () => {
      icon.value = null
      if (!props.isNewService) {
        serviceData.value = { ...props.editServiceData }
      }
    })
    const submitServiceBtnLoading = ref(false)
    const onSubmit = async () => {
      submitServiceBtnLoading.value = true
      if (props.isNewService) {
        await store.dispatch('services/addService', {
          service: serviceData.value,
          icon: icon.value,
        })
      } else {
        await store.dispatch('services/updateService', {
          service: serviceData.value,
          icon: icon.value,
        })
      }
      submitServiceBtnLoading.value = false
      emit('refetch-data')
      emit('update:isSideBarActive', false)
    }

    const iconChange = event => {
      [icon.value] = event.target.files
    }

    const statusOptions = [
      { text: 'Active', value: 'ACTIVE' },
      { text: 'Inactive', value: 'INACTIVE' },
    ]

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetServiceData)

    return {
      serviceData,
      icon,
      statusOptions,
      refFormObserver,

      pageTypes,
      onSubmit,
      getValidationState,
      resetForm,
      iconChange,
      submitServiceBtnLoading,
      maxCharacters,
    }
  },
}
</script>

<style lang="scss">

#add-new-service-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
